import { PlayerSwap } from "@/api/privateRequests/sbcs/player-swap";
import { Player } from "@/types/SBCPlayer";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  backendErrorCode,
  errorMessage,
  solverErrorMessage,
} from "@repo/shared/i18n";
import type {
  BackendSbcSolution,
  CheapestCombination,
} from "@repo/shared/types";

import { APIStatus } from "../../api/APIStatus.type";
import * as privateApi from "../../api/privateApi";
import { SBCRewardType } from "../../api/privateRequests/sbcs/getSbcSets";
import { showModal } from "../message/messageSlice";
import { IRootState } from "../store";
import { solvesWithClubSelector, usedFeature } from "../user/userSlice";
import {
  SbcSet,
  applyVote,
  downvote,
  markSbcSolved as updateSbcSolveInSets,
  upvote,
} from "./sbcSetsSlice";

export type Sbc = {
  name: string;
  slug?: string;
  imageURL: string;
  formation: string;
  id: number;
  setId: number;
  requirements: string[];
  solvable: boolean;
  reward?: SBCRewardType;
  isCompleted: boolean;
  price: number;
  sbcsCount: number;
  message?: string;
  description?: string;
  pointsRequired?: number;
  cheapestCombination?: CheapestCombination;
};

/**
 * One squad the backend found, keyed by its first tag (unique: identical
 * squads fold into one entry before they reach us). `formation` is stamped
 * by an accepted swap; the pitch reads the challenge's own.
 */
export type Solution = BackendSbcSolution<Player> & {
  id: string;
  formation?: string;
};

type SbcsState = {
  sbcs: Sbc[];
  sbcSet: SbcSet;
  /** Every pool's squad from the last solve, club, both, market. */
  solutions: Solution[];
  selectedSolutionId: string | null;
  /** The challenge the squads belong to, so a deep link knows whether to solve. */
  solutionSbcId: number | null;
  sbcsStatus: APIStatus;
  solutionStatus: APIStatus;
  solutionError?: string;
  sbcSetStatus: APIStatus;
  currentRequestId: number;
};

const initialState: SbcsState = {
  sbcs: [],
  sbcSet: null,
  sbcsStatus: "idle",
  solutionStatus: "idle",
  sbcSetStatus: "idle",
  solutions: [],
  selectedSolutionId: null,
  solutionSbcId: null,
  currentRequestId: 0,
};

export const markSbcSolved = createAsyncThunk(
  "mark-sbc-solved",
  async (
    {
      sbcId,
      setId,
      solved,
    }: {
      sbcId: number;
      setId: number;
      solved: boolean;
    },
    { dispatch },
  ) => {
    dispatch(updateSbcSolved(sbcId));
    // Notify sbcSetsSlice that one more/less sbc has been solved
    dispatch(updateSbcSolveInSets({ setId, solved, sbcId }));
    await privateApi.markSbcSolved(sbcId, setId, solved);
    return { sbcId, setId, solved };
  },
);

export const fetchSbcs = createAsyncThunk(
  "fetch-sbcs",
  async ({ setId }: { setId: number }, { getState }) => {
    const state = getState() as IRootState;
    const platform = state.userReducer.user.platform || "Playstation";
    const res = await privateApi.getSbcs(setId, platform);
    return res;
  },
);

export const fetchSbc = createAsyncThunk(
  "fetch-sbc",
  async ({ sbcId, setId }: { sbcId: number; setId: number }, { getState }) => {
    const state = getState() as IRootState;
    const platform = state.userReducer.user.platform || "Playstation";
    return privateApi.getSbc(sbcId, setId, platform);
  },
);

export const fetchSbcSet = createAsyncThunk(
  "fetch-sbc-set",
  async ({ setId }: { setId: number }) => {
    return privateApi.getSbcSet(setId);
  },
);

export const playerSwap = createAsyncThunk(
  "player-swap",
  async (
    { id, solutionPlayers }: { id: number; solutionPlayers: Player[] },
    { getState },
  ): Promise<PlayerSwap[][]> => {
    const state = getState() as IRootState;
    const {
      useUntradeablesOnly,
      excludeActiveSquad,
      excludeObjectivePlayers,
      excludeSbcPlayers,
      includeTransferlist: includeTransferList,
      prioritizeDuplicates,
      prioritizeSbcStorage,
      minRating,
      maxRating,
    } = state.sbcFiltersReducer;
    const { excludedPlayers } = state.sbcFiltersReducer;
    const excludedPlayersMapped = excludedPlayers.flatMap((p) =>
      p ? p.resourceId : [],
    );
    const playerIds = (solutionPlayers || []).map((p) => p.resource_id);

    const res = await privateApi.playerSwap({
      id,
      untradeable: useUntradeablesOnly,
      excludeActiveSquad,
      excludeObjectivePlayers,
      excludeSbcPlayers,
      includeTransferList,
      prioritizeDuplicates,
      prioritizeSbcStorage,
      playerIds,
      excludedPlayers: excludedPlayersMapped,
      minRating,
      maxRating,
      // Two club cards and two market cards per slot with a club; market alone without.
      playersFrom: solvesWithClubSelector(state) ? "both" : "market",
    });
    return res;
  },
);

export const solveSbc = createAsyncThunk(
  "solve-sbc",
  async (sbcId: number, { getState, dispatch }) => {
    const state = getState() as IRootState;
    const requestId = state.sbcsReducer.currentRequestId;
    const {
      useUntradeablesOnly,
      excludeActiveSquad,
      excludeObjectivePlayers,
      excludeSbcPlayers,
      includeTransferlist: includeTransferList,
      prioritizeDuplicates,
      prioritizeSbcStorage,
      minRating,
      maxRating,
    } = state.sbcFiltersReducer;

    const { includedPlayers, excludedPlayers } = state.sbcFiltersReducer;
    const includedPlayersMapped = includedPlayers.flatMap((p) =>
      p ? p.resourceId : [],
    );
    const excludedPlayersMapped = excludedPlayers.flatMap((p) =>
      p ? p.resourceId : [],
    );

    try {
      const res = await privateApi.solveSbcSolutions<Player>(sbcId, {
        untradeable: useUntradeablesOnly,
        excludeActiveSquad,
        excludeObjectivePlayers,
        excludeSbcPlayers,
        includeTransferList,
        prioritizeDuplicates,
        prioritizeSbcStorage,
        includedPlayers: includedPlayersMapped,
        excludedPlayers: excludedPlayersMapped,
        minRating,
        maxRating,
      });

      if (!res.solutions?.length) {
        throw new Error(errorMessage(undefined, "solver"));
      }
      dispatch(usedFeature({ feature: "SOLVE_SBC" }));
      const setId = state.sbcsReducer.sbcs.find((s) => s.id === sbcId)?.setId;
      if (setId != null) {
        privateApi.postEvent({ event: "sbc-solution", sbcSetId: setId, sbcId });
      }
      const solutions: Solution[] = res.solutions.map((solution) => ({
        ...solution,
        id: solution.tags[0],
      }));
      return { solutions, requestId };
    } catch (error) {
      if (
        error.response?.status === 403 &&
        error.response?.data?.error?.message === "ANONYMOUS_SOLVE_LIMIT_REACHED"
      ) {
        dispatch(
          showModal({
            modalType: "login-required",
            freeFeatureLoggedInRequiredHook: "solve-sbc-limit",
          }),
        );
        throw new Error(errorMessage("ANONYMOUS_SOLVE_LIMIT_REACHED"));
      }
      // Gate codes have their own copy; anything else, the solver's own 4xx
      // body included, resolves under errors:solver.* and never prints raw.
      const code = backendErrorCode(error);
      throw new Error(
        code === "SIGN_IN_REQUIRED_TO_INCLUDE_OR_EXCLUDE_PLAYERS"
          ? errorMessage(code)
          : solverErrorMessage(error.response?.data?.error),
      );
    }
  },
);

const sbcsSlice = createSlice({
  name: "sbcs",
  initialState,
  reducers: {
    updateSbcsSolved(
      state,
      action: PayloadAction<{ setId: number; solved: boolean }>,
    ) {
      const { setId, solved } = action.payload;
      state.sbcs.forEach((sbc) => {
        if (sbc.setId === setId) {
          sbc.isCompleted = solved;
        }
      });
    },
    updateSbcSolved(state, action: PayloadAction<number>) {
      const sbcId = action.payload;
      const sbc = state.sbcs.find((sbc) => sbc.id === sbcId);
      if (sbc) {
        sbc.isCompleted = !sbc.isCompleted;
      }
    },
    /** An accepted swap: patches that entry in place, so it survives switching away and back. */
    updateSolution(state, action: PayloadAction<Solution>) {
      state.solutions = state.solutions.map((solution) =>
        solution.id === action.payload.id ? action.payload : solution,
      );
    },
    selectSolution(state, action: PayloadAction<string>) {
      state.selectedSolutionId = action.payload;
    },
    updateSolutionError(state, action: PayloadAction<string>) {
      state.solutionError = action.payload;
    },
    clearSbcs(state) {
      state.sbcs = [];
      state.sbcSetStatus = "idle";
      state.sbcsStatus = "idle";
    },
    clearSolution(state) {
      state.solutions = [];
      state.selectedSolutionId = null;
      state.solutionSbcId = null;
      state.solutionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSbcs.fulfilled, (state, action) => {
        state.sbcs = action.payload;
        state.sbcsStatus = "fulfilled";
      })
      .addCase(fetchSbc.fulfilled, (state, action) => {
        state.sbcs = [action.payload];
        state.sbcsStatus = "fulfilled";
      })
      .addCase(solveSbc.fulfilled, (state, action) => {
        if (state.currentRequestId === action.payload.requestId) {
          const { solutions } = action.payload;
          state.solutions = solutions;
          // Every solve re-adopts the backend's pick, as the Streamline slice does.
          state.selectedSolutionId = (
            solutions.find((solution) => solution.recommended) ?? solutions[0]
          ).id;
          state.solutionStatus = "fulfilled";
          state.solutionError = null;
        }
      })
      .addCase(solveSbc.pending, (state, action) => {
        state.currentRequestId = state.currentRequestId + 1;
        state.solutionSbcId = action.meta.arg;
        state.solutionStatus = "pending";
      })
      .addCase(solveSbc.rejected, (state, action) => {
        state.solutionError = action.error?.message;
        state.solutionStatus = "rejected";
      })
      .addCase(
        fetchSbcSet.fulfilled,
        (state, action: PayloadAction<SbcSet>) => {
          state.sbcSet = action.payload;
          state.sbcSetStatus = "fulfilled";
        },
      )
      .addCase(fetchSbcSet.pending, (state) => {
        state.sbcSetStatus = "pending";
      })
      .addCase(fetchSbcSet.rejected, (state) => {
        state.sbcSetStatus = "rejected";
      })
      /* A vote cast on the set page: the set is only in this slice when the
         visitor arrived without the set list, e.g. from the front page. */
      .addCase(upvote, (state, action) => {
        if (+state.sbcSet?.id === action.payload.setId)
          applyVote(state.sbcSet, "UPVOTE");
      })
      .addCase(downvote, (state, action) => {
        if (+state.sbcSet?.id === action.payload.setId)
          applyVote(state.sbcSet, "DOWNVOTE");
      });
  },
});

export const getSbcsSelector = (state: IRootState) => state.sbcsReducer;
/** The squad on screen: the selected entry of the last solve. */
export const selectedSolutionSelector = (state: IRootState) =>
  state.sbcsReducer.solutions.find(
    (solution) => solution.id === state.sbcsReducer.selectedSolutionId,
  ) ?? null;
export const getSbcSetSelector = (setId: number) => (state: IRootState) => {
  let sbcSet = state.sbcsSetsReducer.sbcsSets.data.find(
    (sbcSet) => sbcSet.id === setId,
  );
  return sbcSet || state.sbcsReducer.sbcSet;
};
export const getSbcSelector =
  (sbcId: number, setId: number) => (state: IRootState) => {
    let sbc = state.sbcsReducer.sbcs.find(
      (sbc) => sbc.id === sbcId && sbc.setId === setId,
    );
    return sbc;
  };

export const {
  updateSbcSolved,
  clearSbcs,
  updateSolution,
  selectSolution,
  clearSolution,
  updateSbcsSolved,
  // setCurrrentSbc,
  // setCurrrentSbcSet,
} = sbcsSlice.actions;
export default sbcsSlice.reducer;
